import { LinkedListWithLayoutSettings } from '@Components/LinkedListWithLayoutSettings';
import { PermissionsList } from '@Components/Permissions';
import { PERMISSIONS_API } from '@Constants/backendConstants';
import { getFrameLabel } from '@Constants/getFrameLabel';
import { Permission } from '@Constants/Permission.g';
import { externalApplicationColumns, userColumns } from '@Constants/TableColumns';
import { useHasPermission } from '@Contexts/appContext';
import { useGroupPermissions } from '@Hooks/Permissions';
import {
    type ApiExternalApplication,
    type ApiGroup,
    type ApiUser,
    EnumFrontendLayoutSetting,
    GroupLinkLists,
} from '@Model';
import {
    Card,
    EditFrameMode,
    EditFrameShell,
    FrameEditBase,
    Group,
    type IFrameBodyProps,
    type IFrameEditShellProps,
    MultiPageView,
    TextInput,
    useFrameEditBaseNavigation,
    useGuiBinder,
    useLinkedList,
} from '@synqo/frontend-component-library';
import { useParams } from 'react-router';
import { FrameEditExternalApplication } from './FrameEditExternalApplication';
import { FrameEditUser } from './FrameEditUser';

export function FrameEditGroup(props: IFrameEditShellProps<ApiGroup>) {
    const hasPermission = useHasPermission();
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    return (
        <FrameEditBase<ApiGroup>
            apiResourcePath={`${PERMISSIONS_API}/groups`}
            renderer={FrameEditGroupBody}
            key={primaryKey}
            primaryKey={primaryKey}
            embedded={props.embedded || props.primaryKey !== undefined}
            canEdit={props.canEdit !== false && hasPermission(Permission.PermissionsGroupsUpdate)}
            canCreate={props.canCreate !== false && hasPermission(Permission.PermissionsGroupsCreate)}
            canDelete={props.canDelete !== false && hasPermission(Permission.PermissionsGroupsDelete)}
            frameProps={{
                events: {
                    ...frameEditBaseNavigationEvents,
                    ...props.events,
                },
            }}
        />
    );
}

function FrameEditGroupBody(props: IFrameBodyProps<ApiGroup>) {
    const hasPermission = useHasPermission();

    const apiGroup = props.data;
    const baseApiObjectPath = `${props.apiResourcePath}/${props.data.id}`;

    const canEdit = props.mode !== EditFrameMode.View;
    const binder = useGuiBinder<ApiGroup>({
        ...props,
        canEdit,
    });

    const { updatePermissions } = useGroupPermissions(props.requestUpdateState);

    const {
        refetchRef: refetchUsersRef,
        linkListApi: linkUsersApi,
        linkedObjectOverlayEvents: linkUsersOverlayEvents,
    } = useLinkedList<ApiGroup, ApiUser>(
        baseApiObjectPath,
        GroupLinkLists.users,
        canEdit && hasPermission(Permission.PermissionsUsersCreate),
        FrameEditUser,
    );

    const {
        refetchRef: refetchExternalApplicationsRef,
        linkListApi: linkExternalApplicationsApi,
        linkedObjectOverlayEvents: linkExternalApplicationsOverlayEvents,
    } = useLinkedList<ApiGroup, ApiExternalApplication>(
        baseApiObjectPath,
        GroupLinkLists.external_applications,
        canEdit && hasPermission(Permission.PermissionsExternalApplicationsCreate),
        FrameEditExternalApplication,
    );

    return (
        <EditFrameShell
            headerProps={{
                label: getFrameLabel('Benutzergruppe', props.mode),
                resourceLabel: 'Benutzergruppe',
                ...props.events,
            }}
            bannerProps={{
                mode: props.mode,
                loading: props.stateQueueDepth > 0,
                ...props.events,
            }}
        >
            <Card name="PrimaryData" border={'withoutBorder'}>
                <TextInput {...binder.bindToProp<string>('label')} label={'Name'} />
            </Card>
            <Card name="links" label="Verknüpfungen" maxWidth={'80rem'}>
                <MultiPageView name="links">
                    <Group name="users" label="Benutzer" key="users">
                        <LinkedListWithLayoutSettings<ApiUser>
                            layoutSettingsContext={EnumFrontendLayoutSetting.UsersTableLayout}
                            layoutSettingsModalContext={EnumFrontendLayoutSetting.UsersTableLayout}
                            {...binder.bindToLinkList(linkUsersApi)}
                            {...linkUsersOverlayEvents}
                            refetchRef={refetchUsersRef}
                            columns={userColumns}
                        />
                    </Group>
                    <Group name="applications" label="Externe Anwendungen" key="applications">
                        <LinkedListWithLayoutSettings<ApiExternalApplication>
                            layoutSettingsContext={EnumFrontendLayoutSetting.ExternalApplicationsTableLayout}
                            layoutSettingsModalContext={EnumFrontendLayoutSetting.ExternalApplicationsTableLayout}
                            {...binder.bindToLinkList(linkExternalApplicationsApi)}
                            {...linkExternalApplicationsOverlayEvents}
                            refetchRef={refetchExternalApplicationsRef}
                            columns={externalApplicationColumns}
                        />
                    </Group>
                </MultiPageView>
            </Card>
            <Card name="Rechte" label="Rechte" maxWidth={'80rem'}>
                <PermissionsList
                    linkedPermissions={apiGroup.permissions ?? []}
                    updatePermissions={props.mode !== EditFrameMode.View ? updatePermissions : undefined}
                />
            </Card>
        </EditFrameShell>
    );
}
